/**
 * @ComponentFor ImboxBlockViewModel
 */

import React from 'react';
import { styled } from '@glitz/react';
import { LinksContainer } from '@polarnopyret/scope';
import BlockPropType from './ImboxBlockViewModel.type';
import { isCompact } from 'Shared/Viewport';
import Accordion from 'Shared/Accordion';
import Input from 'Shared/Fields/Text';
import A from 'Shared/Generic/A';
import connect from 'Shared/connect';
import * as style from 'Shared/Style';
import { Search16x16 } from 'Shared/Icons/Search';
import { Return24x24 } from 'Shared/Icons/Return';
import { Payment24x24 } from 'Shared/Icons/Payment';
import { Freight24x24 } from 'Shared/Icons/Freight';
import { Cart24x24 } from 'Shared/Icons/Cart';
import { User24x24 } from 'Shared/Icons/User';
import { Close16x16 } from 'Shared/Icons/Close';
import { Plus12x12 } from 'Shared/Icons/Plus';
import { Minus12x12 } from 'Shared/Icons/Minus';
import { Assortment24x24 } from 'Shared/Icons/Assortment';
import Loader from 'SiteLayout/Loader';
import { H2, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { GridWrap } from 'Shared/PageLayout';
import { BorderStyle } from 'Shared/Style';

// tslint:disable-next-line:jsx-key
const icons = [
  <Cart24x24 />,
  <Payment24x24 />,
  <Assortment24x24 />,
  <Freight24x24 />,
  <Return24x24 />,
  <User24x24 />,
];

type ConnectedPropType = {
  currentBreakpoint: number;
};

type PropType = BlockPropType & ConnectedPropType;

type StateType = {
  categories: string[];
  articles: ArticleType[][];
  selectedIds: string[];
  allArticleTexts: TextType[];
  articleLimit: boolean[];
  searchPhrase: string;
  searchResult: string[];
  noResults: boolean;
  isLoading: boolean;
};

type TextType = {
  id: string;
  text: string;
};

type ArticleType = {
  id: string;
  qSub: string;
};

type CountrySettingsType = {
  [key: string]: {
    headers: string[];
    categories: string[];
  };
};

const countrySettings: CountrySettingsType = {
  '1319': {
    headers: [
      'Order & Beställning',
      'Betalning & Faktura',
      'Sortiment',
      'Frakt & Leverans',
      'Retur & Reklamation',
      'Medlem',
    ],
    categories: ['712', '755', '758', '754', '756', '1256'],
  },
  '1238': {
    headers: [
      'Ordre og bestilling',
      'Betaling og faktura',
      'Sortiment',
      'Frakt og levering',
      'Retur og reklamasjon',
      'Medlem',
    ],
    categories: ['849', '847', '851', '848', '850', '1257'],
  },
  '1325': {
    headers: ['Order', 'Payment', 'Our products', 'Delivery', 'Return', 'Other questions'],
    categories: ['1104', '1103', '1105', '1106', '1107', '1108'],
  },
  // '2994': {
  //   headers: ['Order', 'Payment', 'Our products', 'Delivery', 'Return', 'Other questions'],
  //   categories: ['1103', '1104', '1105', '1106', '1107', '1108'],
  // },
  '2994': {
    headers: ['Delivery', 'Returns', 'Membership', 'General'],
    categories: ['9194', '9195', '9196', '9197'],
  },
  '1576': {
    headers: ['Tilaus & Maksaminen', 'Maksu & Lasku', 'Tuotteet', 'Toimitus', 'Palautus & Reklamaatio', 'Jäsenyys'],
    categories: ['2403', '2400', '2405', '2401', '2404', '2402'],
  },
  '2010': {
    headers: ['Bestellung', 'Zahlung', 'Unsere Produkte', 'Lieferung', 'Retouren', 'Mitgliedschaft'],
    categories: ['4043', '4044', '4047', '4048', '4049', '4050'],
  },
};

const Container = styled.div({
  display: 'block',
});

const IconItem = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const IconWrapper = styled.div({
  lineHeight: 0,
  fontSize: 32,
});

const AccordionContent = styled.div({
  fontWeight: 500,
  lineHeight: '130%',
});

const ShowMoreOrLess = styled(A, {
  textDecoration: 'none',
  textTransform: 'uppercase',
  color: style.colors.monochrome.a11ygrey,
  fontSize: 14,
  fontWeight: 500,
  display: 'inline-flex',
  ':hover': {
    textDecoration: 'none',
  },
});

const PlusIcon = styled(Plus12x12, {
  marginLeft: 6,
  marginTop: '4px'
});

const Reduce = styled(Minus12x12, {
  marginLeft: 6,
  marginTop: '4px'
});

class CustomerService extends React.Component<PropType, StateType> {
  mounted = false;
  state = {
    categories: [] as string[],
    articles: [] as ArticleType[][],
    allArticleTexts: [] as TextType[],
    selectedIds: [] as string[],
    articleLimit: [true, true, true, true, true, true] as boolean[],
    searchPhrase: '',
    searchResult: [] as string[],
    noResults: false,
    isLoading: false,
  };

  async componentWillMount() {
    this.getCategories();
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  toggleShowMoreOrLess(index: number) {
    const articleIndexes = [...this.state.articleLimit];
    articleIndexes[index] = !articleIndexes[index];
    this.setState({
      articleLimit: articleIndexes,
    });
    return false;
  }

  createMarkup(articleId: string) {
    const article = this.state.allArticleTexts.filter((item) => item.id === articleId);
    if (!article[0].text.includes('<script>' || '</script>')) {
      return { __html: article[0].text };
    }
  }

  async getArticle(id: string) {
    this.setState({
      isLoading: true,
    });
    const alreadyExists = this.state.allArticleTexts.find((item) => item.id === id);
    if (!alreadyExists && this.mounted) {
      try {
        const article = await fetch(`${this.props.apiUrl}/${this.props.apiKey}/articles/${id}/view`);
        const data = await article.json();
        const newArticle = {
          id: data.id,
          text: data.qBod,
        };
        this.setState((prevState) => ({
          selectedIds: [...prevState.selectedIds, id],
          allArticleTexts: [...prevState.allArticleTexts, newArticle],
          isLoading: false,
        }));
      } catch (e) {
        console.error(e);
      }
    } else if (this.mounted && alreadyExists && this.state.selectedIds.find((checkId) => checkId === id)) {
      this.setState({
        selectedIds: this.state.selectedIds.filter((existingId) => id !== existingId),
        isLoading: false,
      });
    } else {
      if (this.mounted) {
        this.setState((prevState) => ({
          selectedIds: [...prevState.selectedIds, id],
          isLoading: false,
        }));
      }
    }
  }

  async getArticleHeaders() {
    // Article categories that could not be fetched from the API
    const categories = countrySettings[this.props.apiKey].categories;
    try {
      const allCategories = Promise.all(
        categories.map(async (category) => {
          const result = await fetch(`${this.props.apiUrl}/${this.props.apiKey}/categories/${category}/articles`).then(
            (response) => {
              return response.json();
            },
          );
          return result;
        }),
      );
      return allCategories;
    } catch (e) {
      console.error(e);
    }
  }

  async getCategories() {
    const headers = countrySettings[this.props.apiKey].headers;
    this.setState(
      {
        categories: headers,
        isLoading: true,
      },
      () => {
        this.getArticleHeaders().then((data) => {
          if (this.mounted) {
            this.setState({
              articles: data,
              isLoading: false,
            });
          }
        });
      },
    );
  }

  onSearchChange(e: React.FormEvent<HTMLElement>) {
    this.setState({
      searchPhrase: (e.target as HTMLInputElement).value,
    });
  }

  async search() {
    const setIndexes = [...this.state.articleLimit];
    const newIndexes = setIndexes.fill(false);
    this.setState({
      isLoading: true,
    });

    try {
      const response = await fetch(
        `https://apiv2.imbox.io/message/searchq/${this.props.apiKey}/${this.state.searchPhrase}/?callback=cb`,
      );
      const result = await response.text();

      const init = result.indexOf('(');
      const fin = result.lastIndexOf(')');
      const data = JSON.parse(result.substr(init + 1, fin - init - 1));

      if (data.json.length > 0) {
        const articleIds = await data.json.map((article: any) => {
          return article._source.article_id;
        });
        if (this.mounted) {
          this.setState({
            searchResult: articleIds,
            articleLimit: newIndexes,
            noResults: false,
            isLoading: false,
          });
        }
      } else {
        if (this.mounted) {
          this.setState({
            noResults: true,
            isLoading: false,
          });
        }
      }
    } catch (err) {
      if (this.mounted) {
        console.error(err);
        this.setState({
          searchResult: [],
          noResults: false,
          isLoading: false,
        });
      }
    }
  }

  clear() {
    this.setState({
      searchResult: [],
      searchPhrase: '',
      articleLimit: [true, true, true, true, true, true],
      noResults: false,
    });
  }

  handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      this.search();
    }
  }

  printHeader(index: number) {
    if (this.state.searchResult.length > 0) {
      const containsArticles = this.state.articles[index].map((article) =>
        this.state.searchResult.includes(article.id),
      );
      if (containsArticles.includes(true)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  render() {
    const isMobile = isCompact(this.props.currentBreakpoint);

    return (
      <GridWrap className="grid">
        <Container className={createGridClasses(12, 11, createOffsetGridClassLarge(2))}>
          <Loader visible={this.state.isLoading} />
          <div className='grid subgrid-11'>
            <div className={createGridClasses(12, 5)}>
              <Input
                value={this.state.searchPhrase}
                type={'search'}
                onChange={(e) => this.onSearchChange(e)}
                onKeyPress={(e) => this.handleKeyPress(e)}
                placeholder={this.props.searchPlaceholder}
                icon={this.state.searchResult.length === 0 ? Search16x16 : Close16x16}
                onIconClick={() => (this.state.searchResult.length < 1 ? this.search() : this.clear())}
                hideLabel
                borderStyle={BorderStyle.BlackThickSolid}
                css={{
                  ...(!isMobile && {
                    display: 'block',
                  }),
                }}
              />

              {this.state.noResults && (
                <styled.Span>{this.props.labels.noResultsFor + ' ´' + this.state.searchPhrase + '´'}</styled.Span>
              )}
            </div>
          </div>
          {this.state.categories &&
            !this.state.noResults &&
            this.state.articles.length > 0 &&
            this.state.categories.map((category, index: number) => {
              const limit = this.state.articleLimit[index];
              const baseArticles = this.state.articles[index];
              const categoryArticles =
                this.state.searchResult.length > 0
                  ? baseArticles.filter((x) => this.state.searchResult.includes(x.id))
                  : limit
                    ? baseArticles.slice(0, 4)
                    : baseArticles;

              return (
                <styled.Div
                  key={category}
                  className="grid subgrid-11"
                  css={{
                    ...(categoryArticles?.length > 0 && {
                      border: isMobile
                        ? {}
                        : {
                          bottom: {
                            color: 'black',
                            width: 1,
                            style: 'solid',
                          },
                        },
                      padding: {
                        y: isMobile ? 64 : 80,
                      },
                    }),
                  }}
                >
                  <styled.Div
                    className={createGridClasses(12, 5)}
                    css={{
                      paddingBottom: isMobile ? 32 : 0,
                    }}
                  >
                    {this.printHeader(index) && (
                      <IconItem>
                        <IconWrapper>{icons[index]}</IconWrapper>
                        <H2
                          fontSize={ResponsiveSize.D32_M24}
                          upperCase={false}
                          css={{
                            marginLeft: '16px',
                            fontWeight: 450,
                          }}
                        >
                          {category}
                        </H2>
                      </IconItem>
                    )}
                  </styled.Div>
                  <div className={createGridClasses(12, 6, createOffsetGridClassLarge(6))}>
                    {categoryArticles.map((article: ArticleType) => {
                      return (
                        <div key={article.id}>
                          {this.state.searchResult.length < 1 ? (
                            <Accordion
                              hasLinks={false}
                              header={article.qSub}
                              fontWeight={450}
                              onToggle={() => {
                                this.getArticle(article.id);
                              }}
                              isCompact={isMobile}
                              isOpen={!!this.state.selectedIds.find((id) => id === article.id)}
                            >
                              <AccordionContent>
                                {this.state.allArticleTexts.find((item) => item.id === article.id) && (
                                  <LinksContainer>
                                    <div dangerouslySetInnerHTML={this.createMarkup(article.id)} />
                                  </LinksContainer>
                                )}
                              </AccordionContent>
                            </Accordion>
                          ) : (
                            <div key={article.id}>
                              {this.state.searchResult.includes(article.id) && (
                                <Accordion
                                  hasLinks={false}
                                  header={article.qSub}
                                  onToggle={() => {
                                    this.getArticle(article.id);
                                  }}
                                  isCompact={isMobile}
                                  isOpen={!!this.state.selectedIds.find((id) => id === article.id)}
                                >
                                  <AccordionContent>
                                    {this.state.allArticleTexts.find((item) => item.id === article.id) && (
                                      <LinksContainer>
                                        <div dangerouslySetInnerHTML={this.createMarkup(article.id)} />
                                      </LinksContainer>
                                    )}
                                  </AccordionContent>
                                </Accordion>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {this.state.articles[index].length > 4 && this.state.searchResult.length < 1 && (
                      <ShowMoreOrLess onClick={() => this.toggleShowMoreOrLess(index)} to={null}
                        css={{
                          // @ts-ignore
                          ':hover span': {
                            transform: 'rotate(90deg)',
                            transition: {
                              property: 'transform',
                              duration: 300,
                            }
                          }
                        }}
                      >
                        {this.state.articleLimit[index] ? this.props.labels.showAll : this.props.labels.showLess}
                        {this.state.articleLimit[index] ? <PlusIcon /> : <Reduce />}
                      </ShowMoreOrLess>
                    )}
                  </div>
                </styled.Div>
              );
            })}
        </Container>
      </GridWrap>
    );
  }
}

export default connect((state) => ({
  currentBreakpoint: state.currentBreakpoint,
}))(CustomerService);
